<script setup>
// import { useRouter } from "vue-router";
import { useHead } from "@vueuse/head";
import CtaSection from "@/components/molecules/CtaSection.vue";

// const router = useRouter();

// const navigateTo = (path) => {
//   router.push(path);
// };

// SEO構造化データ（Article スキーマ）
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "VTuberクラファンとは？仕組み・費用・事例・始め方をわかりやすく解説",
  description:
    "VTuberクラファンって何？3D化・新衣装・楽曲制作などをファンと一緒に実現するクラウドファンディングの仕組み・費用・成功事例・始め方をわかりやすく解説。",
  datePublished: "2025-10-27",
  dateModified: "2025-10-27",
  author: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  publisher: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  image: "/img/airticlepic/article3.webp",
};

// メタ情報と構造化データを設定
useHead({
  title:
    "VTuberクラファンとは？仕組み・費用・事例・始め方をわかりやすく解説 | SukiSukiらいぶすてーしょん",
  meta: [
    {
      name: "description",
      content:
        "VTuberクラファンって何？3D化・新衣装・楽曲制作などをファンと一緒に実現するクラウドファンディングの仕組み・費用・成功事例・始め方をわかりやすく解説。",
    },
    {
      property: "og:title",
      content:
        "VTuberクラファンとは？仕組み・費用・事例・始め方をわかりやすく解説 | SukiSukiらいぶすてーしょん",
    },
    {
      property: "og:description",
      content:
        "VTuberクラファンって何？3D化・新衣装・楽曲制作などをファンと一緒に実現するクラウドファンディングの仕組み・費用・成功事例・始め方をわかりやすく解説。",
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:image",
      content: "https://sukisuki.live/img/airticlepic/article3.webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(articleSchema),
    },
  ],
});
</script>

<template>
  <v-app>
    <!-- ヒーローセクション -->
    <figure class="hero-section">
      <img
        src="/img/airticlepic/article3.webp"
        alt="VTuberクラファンの概要を解説するヒーロー画像"
      />
    </figure>

    <!-- パンくずナビゲーション -->
    <nav aria-label="パンくずリスト" class="breadcrumb-section">
      <v-container>
        <ol class="breadcrumb">
          <li><a href="/academy">TOP</a></li>
          <li>配信クラファン</li>
        </ol>
      </v-container>
    </nav>

    <!-- メインコンテンツ -->
    <main>
      <article class="article-content">
        <v-container>
          <!-- 記事タイトル -->
          <div class="article-title-box">
            <h1>VTuberクラファンとは？仕組み・費用・事例・始め方をわかりやすく解説</h1>
          </div>

          <!-- 更新日 -->
          <div class="update-date-box">
            <time datetime="2025-10-27">更新日:2025年10月27日</time>
          </div>

          <!-- イントロダクション -->
          <div class="intro-box">
            <p>
              VTuberクラファンって何？3D化・新衣装・楽曲制作などをファンと一緒に実現するクラウドファンディングの仕組み・費用・成功事例・始め方をわかりやすく解説。
            </p>
          </div>

          <section class="learn-section">
            <h2 class="learn-title">この記事でわかる内容</h2>
            <div class="learn-content">
              <p>
                近年、VTuberが新衣装や3Dモデル、オリジナル楽曲などを実現するために「クラウドファンディング（クラファン）」を活用するケースが急増しています。
              </p>
              <p>
                この記事では、「VTuberクラファンとは何か」から「実際の成功事例」「費用の目安」「始め方」までを、わかりやすく解説します。
              </p>
              <p>
                また、記事の後半では配信を通じてファンと一緒に支援を集める“配信クラファン”という新しい形も紹介します。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">VTuberクラファンとは？</h2>
            <div class="learn-content">
              <p>
                クラウドファンディングとは、インターネットを通じて多くのファンから少しずつ資金を募る仕組みです。
                VTuberの場合、主に以下のような目的で活用されています。
              </p>
              <p>
                <span class="bold-text">🎨 新衣装・新Live2D/3Dモデルの制作</span>
              </p>
              <p>
                <span class="bold-text">🎤 オリジナル楽曲・MV制作</span>
              </p>
              <p>
                <span class="bold-text">🎉 リアルイベント開催</span>
              </p>
              <p>
                ファンにとっても、推しの夢を“直接支援できる”貴重な機会となるため、支援が集まりやすいのが特徴です。
              </p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">クラウドファンディングの仕組み</h2>
            <div class="learn-content">
              <p>VTuberのクラファンでは、主に以下の2つの方式が使われます。</p>
            </div>
            <div class="learn-content-table">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>方式</th>
                    <th>内容</th>
                    <th>代表的な用途</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>All or Nothing型</td>
                    <td>目標金額を達成した場合のみ資金を受け取れる</td>
                    <td>明確な目標がある（例：3D化）</td>
                  </tr>
                  <tr>
                    <td>All in型</td>
                    <td>目標未達でも資金を受け取れる</td>
                    <td>継続活動・リターン制作など</td>
                  </tr>
                </tbody>
              </table>
              この仕組みによって、制作費の一部または全額をファンの支援でまかなうことが可能になります。
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">VTuber活動にかかる費用の目安</h2>
            <div class="learn-content">
              <p>VTuber活動には、意外と多くの制作コストや費用がかかります。</p>
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>項目</th>
                    <th>目安費用</th>
                    <th>内容</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>キャラクターデザイン</td>
                    <td>5〜40万円</td>
                    <td>イラスト制作</td>
                  </tr>
                  <tr>
                    <td>Live2Dモデリング</td>
                    <td>10〜40万円</td>
                    <td>2Dのモデル・表情・動きの実装</td>
                  </tr>
                  <tr>
                    <td>3Dモデル制作</td>
                    <td>60〜350万円</td>
                    <td>3Dのモデルの制作・表情・アニメーションの実装</td>
                  </tr>
                  <tr>
                    <td>楽曲・MV制作</td>
                    <td>15〜80万円</td>
                    <td>作曲・映像含む</td>
                  </tr>
                  <tr>
                    <td>リアルイベント開催</td>
                    <td>60万円〜400万円</td>
                    <td>会場・メンバー・ディレクター・音響などの費用</td>
                  </tr>
                </tbody>
              </table>
              クラファンはこれらの制作費をファンと一緒に実現できる手段です。
            </div>
          </section>

          <!-- CTA ボタン -->
          <CtaSection article-id="academy-article-3" />

          <section class="learn-section">
            <h2 class="learn-title">【最新】VTuberクラファンの成功事例（2023〜2025年）</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">① 凪乃ましろ｜3D化プロジェクト（CAMPFIRE）</span>
              </p>
              <p><span class="bold-text">目標金額：</span>500万円</p>
              <p><span class="bold-text">支援総額：</span>3,369万円（達成率673%／支援者1,324人）</p>

              <p>
                <span class="bold-text">概要：</span
                >歌うVTuberとして活動する凪乃ましろさんが3Dモデル化を目指して実施。
              </p>
              <p>
                <span class="bold-text">成功要因：</span>
              </p>
              <p>・明確な目的（3D化）</p>
              <p>・ファンと進行を共有する“参加型”の進行設計</p>
              <p>・リターンが充実（お礼動画・限定ボイス・グッズ）</p>
              <p>👉 VTuber界隈で最も注目を集めたクラファン事例のひとつです。</p>

              <p>
                <span class="bold-text"
                  >② 桜神くおん｜新Live2Dモデル化プロジェクト（うぶごえ）</span
                >
              </p>
              <p><span class="bold-text">目標金額：</span>200万円</p>
              <p><span class="bold-text">支援総額：</span>346万円（達成率173%／支援者139人）</p>
              <p>
                <span class="bold-text"> 概要：</span>Live2Dモデルの新規制作資金をクラファンで募集。
              </p>
              <p>
                <span class="bold-text">成功要因：</span>
              </p>
              <p>・達成可能な現実的目標設定</p>
              <p>・“モデルアップグレード”というファンが共感しやすいゴール</p>
              <p>・高額支援者向けリターンの工夫</p>
              <p>
                <span class="bold-text">③ ゆいせ｜Live3D化プロジェクト（MotionGallery）</span>
              </p>
              <p><span class="bold-text">目標金額：</span>120万円</p>
              <p><span class="bold-text">支援総額：</span>183万円（達成率152%／支援者23人）</p>
              <p>
                <span class="bold-text"> 概要：3D制作費用をクラファンで募集。</span>
              </p>
              <p>
                <span class="bold-text">成功要因：</span>
              </p>
              <p>・リターンを複数価格帯に設定し、支援しやすさを確保</p>
              <p>・プロジェクト後のスケジュールを公開し、透明性を高めた</p>
              <p>・信頼感とストーリー性の両立がファンの支援意欲を高めた</p>
              <p><span class="bold-text">④ その他の事例（CAMPFIRE・MotionGalleryなど）</span></p>
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>プロジェクト</th>
                    <th>プラットフォーム</th>
                    <th>概要</th>
                    <th>達成率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Vtuberバル子の3D化プロジェクト！</td>
                    <td>MotionGallery</td>
                    <td>3D化プロジェクト</td>
                    <td>126%</td>
                  </tr>
                  <tr>
                    <td>一花アスター再デビュー＆結婚記念ライブ支援プロジェクト</td>
                    <td>CAMPFIRE</td>
                    <td>再デビュー＆記念ライブ</td>
                    <td>180%</td>
                  </tr>
                </tbody>
              </table>
              <p>
                👉 こうした事例を比較しても、VTuberクラファンの中心テーマは「制作費＋ファン共創」。
                金額よりも“応援の物語”が重視される傾向にあります。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">成功事例から見る「共通ポイント」</h2>
            <div class="learn-content">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>成功要因</th>
                    <th>内容</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>🎯 明確なゴール</td>
                    <td>「3D化」「新衣装」など、具体的でファンが想像しやすい目標。</td>
                  </tr>
                  <tr>
                    <td>❤️ ファン参加型設計</td>
                    <td>お披露目配信やリターンで“自分も一緒に叶えた”実感を作る。</td>
                  </tr>
                  <tr>
                    <td>💰 達成可能な目標金額</td>
                    <td>クラファンでは目標金額は現実的で達成可能なものが成功率を高める。</td>
                  </tr>
                  <tr>
                    <td>🎁 リターンの段階設計</td>
                    <td>3,000円〜5万円までなど複数プランを用意。</td>
                  </tr>
                  <tr>
                    <td>📅 スケジュール明示</td>
                    <td>
                      クラファンページでの制作スケジュールリターンスケジュールを公開し、流れを透明化。
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">VTuberクラファンの始め方</h2>
            <div class="learn-content">
              <p>実際にクラファンを始める場合の流れは次の通りです。</p>
              <p>
                <span class="bold-text">1. 目的を決める</span
                >（例：新衣装制作・3D化・オリジナル楽曲制作など）
              </p>
              <p>
                <span class="bold-text">2. 目標金額を設定する</span>（達成可能な現実的な金額に設定）
              </p>
              <p>
                <span class="bold-text">3. プラットフォームを選ぶ</span
                >（SukiSukiらいぶすてーしょん・CAMPFIRE・うぶごえ・Makuakeなど）
              </p>
              <p>
                <span class="bold-text">4. リターンを設計する</span>（SNSや日々の配信で呼びかけ）
              </p>
              <p>
                <span class="bold-text">5. ページを公開・宣伝</span
                >（プロジェクトページを作成し、公開）
              </p>
              <p>
                <span class="bold-text">6. 終了後の報告・お礼配信を行う</span
                >（配信を通してリターンや活動の背景をリアルタイムで説明）
              </p>
              <p>
                👉 より詳しい始め方は、関連記事
                <a href="/academy/how-to-start-crowdfunding-for-vtubers"
                  >VTuber・ライバーのクラファンの始め方-クラウドファンディングの始め方をわかりやすく解説</a
                >
                で詳しく紹介しています。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">新しい形：「配信クラファン」という進化形</h2>
            <div class="learn-content">
              <p>
                SukiSukiらいぶすてーしょんの「配信クラファン」では、従来のクラファンのように「ページを作って待つ」のではなく、
                配信中にファンと一緒に支援を集める“リアルタイム型クラファン”を実現しています。
              </p>
              <p>🔴 配信画面上で支援がリアルタイム表示</p>
              <p>🙌 支援してくれたファンにその場でお礼可能</p>
              <p>👤 面倒な企画立案・ページデザインなどをすべて代行</p>
              <p>🧾 リターングッズ制作・発送までワンストップで代行</p>
              <p>💸 初期費用は不要</p>
              <p>
                「クラファンをやりたいけど手続きや準備が大変そう」と感じるVTuber・ライバーにぴったりの仕組みです。
              </p>
            </div>
          </section>
          <!-- CTA ボタン -->
          <CtaSection article-id="academy-article-3" />
          <section class="learn-section">
            <h2 class="learn-title">まとめ</h2>
            <div class="learn-content">
              <p>
                VTuberクラファンは、単なる資金集めではなく「ファンと一緒に夢を叶えるプロセス」です。
                CAMPFIREやうぶごえなど多くの成功事例があり、VTuber文化の中で定番の手段になりつつあります。
              </p>
              <p>
                そしてSukiSukiらいぶすてーしょんの“配信クラファン”は、その体験をもっとリアルタイムに、もっと簡単に実現する新しい形。
              </p>
              <p>
                💡 配信を通じて、ファンと一緒に夢を叶える。
                それがSukiSukiらいぶすてーしょんの「配信クラファン」です。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">出典・参考</h2>
            <div class="learn-content">
              <p>
                <a href="https://camp-fire.jp/projects/682690/" target="_blank"
                  >CAMPFIRE：「凪乃ましろ 3D化プロジェクト」</a
                >
              </p>
              <p>
                <a href="https://ubgoe.com/projects/809" target="_blank"
                  >うぶごえ：「桜神くおん 新Live2Dモデル化プロジェクト」</a
                >
              </p>
              <p>
                <a href="https://motion-gallery.net/projects/yuise_live3d" target="_blank"
                  >MotionGallery：「ゆいせ Live3D化プロジェクト」</a
                >
              </p>
              <p>
                <a href="https://motion-gallery.net/projects/baruko_3D_project" target="_blank"
                  >MotionGallery： Vtuberバル子の3D化プロジェクト！</a
                >
              </p>
              <p>
                <a href="https://camp-fire.jp/projects/854995/" target="_blank"
                  >CAMPFIRE：一花アスター再デビュー＆結婚記念ライブ支援プロジェクト」</a
                >
              </p>
            </div>
          </section>
        </v-container>
      </article>
    </main>

    <!-- CTA ボタン -->
    <CtaSection class="mb-10" article-id="academy-article-3" />
  </v-app>
</template>

<style scoped>
/* ヒーローセクション */
.hero-section {
  padding: 20px;
  width: 100%;
}

.hero-section img {
  width: 100%;
  height: auto;
  display: block;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-main-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.hero-left-content {
  flex: 1;
  text-align: left;
}

.hero-tag {
  background-color: #c8007f;
  color: white;
  padding: 8px 20px;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 20px;
}

.hero-title {
  font-size: 2.5rem;
  color: #6b4c93;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: #6b4c93;
  margin: 0;
}

.hero-right-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-image {
  max-width: 100%;
  height: auto;
}

/* パンくず */
.breadcrumb-section {
  background-color: white;
  /* padding: 15px 0; */
}

.breadcrumb {
  color: #666;
  font-size: 14px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 8px;
  text-align: left;
  display: flex;
  list-style: none;
  margin: 0;
  gap: 8px;
}

.breadcrumb li {
  display: inline-block;
}

.breadcrumb li:not(:last-child)::after {
  content: ">";
  margin-left: 8px;
}

.breadcrumb a {
  color: #666;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* メインコンテンツ */
::v-deep(.v-container) {
  text-align: left;
  padding: 0 !important;
}

.article-content {
  /* margin: 40px 0; */
  text-align: left;
}

.article-content h2,
.article-content h3 {
  text-align: left;
}

.article-title-box {
  margin-bottom: 5px;
}

.article-title-box h1,
.article-title-box h2 {
  color: #333;
  font-size: 18px;
  margin: 0;
  font-weight: bold;
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
  line-height: 1.6;
}

.update-date-box {
  padding: 10px 20px;
  margin-bottom: 10px;
}

.update-date-box p,
.update-date-box time {
  color: #333;
  margin: 0;
  font-size: 14px;
  text-align: left;
}

.intro-box {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  margin-left: 20px;
  margin-right: 20px;
  font-size: 14px;
}

.intro-box p {
  color: #333;
  margin: 0;
  line-height: 1.6;
  text-align: left;
}

.learn-section {
  margin: 30px 0;
}

.learn-title {
  color: #333;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 20px;
  text-align: left;
  background-color: #ffe0e0;
  padding: 15px 0 15px 20px;
  position: relative;
  line-height: 1.6;
}

.learn-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  background-color: #982da6;
}

.learn-content {
  margin-left: 20px;
  text-align: left;
}

.learn-content p {
  color: #333;
  line-height: 1.6;
  margin: 0 20px 15px 0;
  text-align: left;
  font-size: 16px;
}

/* CTA ボタン */
.cta-section {
  padding-bottom: 40px;
  margin-left: 40px;
  margin-right: 40px;
}

.custom-content {
  line-height: 1.6;
}

.bold-text {
  font-weight: bold;
}

/* 比較テーブル */
.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
}

.comparison-table th,
.comparison-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
}

.comparison-table th {
  background-color: #ffe0e0;
  font-weight: bold;
  color: #333;
}

.comparison-table td {
  background-color: #fff;
}

.comparison-table tr:hover {
  background-color: #f9f9f9;
}

.learn-content-table {
  margin-left: 20px;
  margin-right: 20px;
  text-align: left;
  line-height: 1.6;
}

@media (min-width: 750px) {
  .hero-section {
    padding: 0 25%;
  }

  .hero-section img {
    width: 100%;
    height: auto;
    display: block;
  }
  .breadcrumb {
    padding: 10px 25%;
  }
  .v-container {
    max-width: 5000px !important;
  }
  .article-title-box {
    padding: 0 25%;
  }
  .article-title-box h1,
  .article-title-box h2 {
    font-size: 20px;
    padding: 0;
    line-height: 1.6;
  }
  .update-date-box {
    padding: 10px 25%;
  }
  .update-date-box p,
  .update-date-box time {
    padding: 0;
  }
  .intro-box {
    padding: 10px;
  }
  .intro-box p {
    font-size: 16px;
    line-height: 1.6;
  }
  .intro-box {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-title {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content p {
    margin-left: 0;
    margin-right: 0;
  }
  .cta-section {
    margin-left: 30%;
    margin-right: 30%;
  }
  .learn-content-table {
    margin-left: 25%;
    margin-right: 25%;
    text-align: left;
  }
}
</style>
